'use client';

import React, { useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Checkboxes,
  FieldsetPassword,
  Heading,
  Input,
  InputAction,
  DateInputPopover,
  Radios,
  Range,
  Select,
  Textarea,
  ToggleSwitch,
} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Container } from '@layout';
import MarkupSnippet from '../markup-snippet/MarkupSnippet';

export interface FormControlsProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  cardClassName?: string;
  isSpanish?: boolean;
}

const FormControls: React.FC<FormControlsProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
  cardClassName,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const [rangeValue, setRangeValue] = useState(50);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [selectedOption, setSelectedOption] = useState(isSpanish ? 'Opción 1' : 'Option 1');
  // Checkboxes use option.id as the internal value, so keep these values stable across languages.
  const [selectedValues, setSelectedValues] = useState<string[]>(['option1']);
  const [switchEnabled, setSwitchEnabled] = useState(false);
  const [toggleEnabled, setToggleEnabled] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  return (
    <section {...withOptionalId(sectionID)} className={classes || 'form-controls section'}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading
          as='h3'
          title={t('Forms & Inputs', 'Formularios e Inputs')}
          description={t(
            'Flexible form and input system supporting text, selects, checkboxes, radios, sliders, file uploads, and validation. All controls are accessible, customizable, and support helpful messaging and state via props.',
            'Sistema flexible de formularios e inputs con soporte para texto, selects, checkboxes, radios, sliders, carga de archivos y validacion. Todos los controles son accesibles, personalizables y permiten mensajes de ayuda y estado mediante props.'
          )}
        />
        <div className='form-demo'>
          <Card className={cardClassName}>
            <div className='card-header'>
              <Heading as='h4'>{t('Example: inline review', 'Ejemplo: revision en linea')}</Heading>
            </div>
            <p className='small' style={{ color: 'var(--muted)', marginTop: 0 }}>
              {t(
                'Label, helper text, validation, and actions as they appear in a real panel.',
                'Etiqueta, texto de ayuda, validacion y acciones como en un panel real.'
              )}
            </p>
            <div className='form-grid'>
              <fieldset>
                <Input
                  id='forms-demo-project'
                  name='forms-demo-project'
                  type='text'
                  label={t('Project name', 'Nombre del proyecto')}
                  defaultValue={t('Website refresh', 'Renovacion web')}
                  hint={t('Visible to your team only.', 'Visible solo para tu equipo.')}
                />
              </fieldset>
              <fieldset>
                <Input
                  id='forms-demo-owner-email'
                  name='forms-demo-owner-email'
                  type='email'
                  className='is-invalid'
                  defaultValue='not-an-email'
                  label={t('Owner email', 'Correo del responsable')}
                  hintClassName='error'
                  hint={t('Enter a valid email address.', 'Ingresa un correo valido.')}
                />
              </fieldset>
              <fieldset className='field-col'>
                <div
                  className='row btn-row gap'
                  style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
                >
                  <Button type='button' label={t('Cancel', 'Cancelar')} className='btn-ghost' />
                  <Button
                    type='button'
                    label={t('Save changes', 'Guardar cambios')}
                    className='btn-primary'
                  />
                </div>
              </fieldset>
            </div>
          </Card>

          <Card className={cardClassName}>
            <form id='contactForm'>
              <div className='card-header'>
                <Heading as='h4'>{t('Contact form', 'Formulario de contacto')}</Heading>
              </div>
              <div className='form-grid'>
                <fieldset>
                  <Input
                    id='name'
                    name='name'
                    type='text'
                    placeholder={t('Full Name', 'Nombre completo')}
                    required
                    label={t('Full name', 'Nombre completo')}
                    hint={t(
                      'Use your legal name for agreements.',
                      'Usa tu nombre legal para acuerdos.'
                    )}
                  />
                </fieldset>
                <fieldset>
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    placeholder='email@domain.com'
                    required
                    label={t('Work email', 'Correo laboral')}
                    hint={t(
                      'We only email about your request.',
                      'Solo enviamos correos sobre tu solicitud.'
                    )}
                  />
                </fieldset>
                <fieldset>
                  <Input
                    id='org'
                    name='org'
                    type='text'
                    placeholder={t('Organization', 'Organizacion')}
                    label={t('Organization', 'Organizacion')}
                  />
                </fieldset>
                <fieldset>
                  <Select
                    id='role'
                    name='role'
                    label={t('Role', 'Rol')}
                    options={[
                      {
                        value: '',
                        label: t('Select Role', 'Selecciona un rol'),
                        placeholder: true,
                      },
                      {
                        value: 'manager-member',
                        label: t('Manager, Member Manager', 'Gerente, gestor de miembros'),
                      },
                      {
                        value: 'manager-engineering',
                        label: t('Manager, Engineering', 'Gerente de ingenieria'),
                      },
                      {
                        value: 'study-coordinator',
                        label: t('Study Coordinator', 'Coordinador de estudio'),
                      },
                      { value: 'qa-compliance', label: t('QA / Compliance', 'QA / Cumplimiento') },
                    ]}
                  />
                </fieldset>
                <fieldset>
                  <Select
                    id='budget'
                    name='budget'
                    label={t('Approx. budget', 'Presupuesto aprox.')}
                    options={[
                      {
                        value: '',
                        label: t('Select Budget', 'Selecciona presupuesto'),
                        placeholder: true,
                      },
                      { value: 'lt-25k', label: '< $25k' },
                      { value: '25-50k', label: '$25k – $50k' },
                      { value: '50-100k', label: '$50k – $100k' },
                      { value: '100k+', label: '$100k+' },
                    ]}
                  />
                </fieldset>
                <DateInputPopover
                  id='start'
                  name='start'
                  label={t('Target start date', 'Fecha objetivo de inicio')}
                />
                <fieldset className='field-col'>
                  <Textarea
                    id='message'
                    name='message'
                    label={t('Project details', 'Detalles del proyecto')}
                    rows={5}
                    placeholder={t(
                      'Briefly describe scope, timelines, and systems to integrate...',
                      'Describe brevemente el alcance, tiempos y sistemas a integrar...'
                    )}
                  />
                </fieldset>
                <FieldsetPassword
                  id='password'
                  htmlFor='password'
                  name='password'
                  required
                  onChange={handleChange}
                  value={password ?? ''}
                  label={t('Password', 'Contrasena')}
                  placeholder={t('Password', 'Contrasena')}
                />
                <fieldset></fieldset>
                <fieldset>
                  <Checkbox
                    id='consent'
                    htmlFor='consent'
                    label={t(
                      'I agree to the processing of my information.',
                      'Acepto el procesamiento de mi informacion.'
                    )}
                    hint={t('Required for contact.', 'Requerido para contacto.')}
                  />
                </fieldset>
                <fieldset>
                  <Button
                    label={` ${t('Send request', 'Enviar solicitud')}`}
                    className='btn-primary btn-center'
                    type='submit'
                  />
                </fieldset>
              </div>
            </form>
          </Card>

          <Card className={cardClassName}>
            <div className='card-header'>
              <Heading as='h4'>{t('Control gallery', 'Galeria de controles')}</Heading>
            </div>
            <div className='control-grid'>
              <InputAction
                id='inputActionExample'
                className='field-span-2'
                label={t('Input with action', 'Input con accion')}
                placeholder={t('Enter a code', 'Ingresa un codigo')}
                buttonLabel={t('Join', 'Unirse')}
                buttonType='button'
                hint={t(
                  'The field and button share one height, with no gap between them.',
                  'El campo y el boton comparten altura, sin espacio entre ellos.'
                )}
              />
              <MarkupSnippet
                className='field-span-2'
                label={t('HTML', 'HTML')}
                copyLabel={t('Copy', 'Copiar')}
                copiedLabel={t('Copied', 'Copiado')}
                code={`<fieldset class="input-action">
  <label for="code">${t('Input with action', 'Input con accion')}</label>
  <div class="controls">
    <input id="code" type="text" placeholder="${t('Enter a code', 'Ingresa un codigo')}" />
    <button class="btn btn-primary" type="button">
      <span class="label">${t('Join', 'Unirse')}</span>
    </button>
  </div>
</fieldset>`}
              />
              <fieldset>
                <Input
                  id='textExample'
                  placeholder={t('Type something…', 'Escribe algo…')}
                  label={t('Text input', 'Input de texto')}
                />
              </fieldset>
              <fieldset>
                <Input
                  id='numberExample'
                  type='number'
                  step={1}
                  min={0}
                  defaultValue={5}
                  label={t('Number', 'Numero')}
                />
              </fieldset>
              <fieldset>
                <Select
                  id='selectExample'
                  label={t('Select', 'Seleccionar')}
                  options={[
                    {
                      value: '',
                      label: t('Select Version', 'Selecciona version'),
                      placeholder: true,
                    },
                    { value: 'alpha', label: t('Alpha', 'Alfa') },
                    { value: 'beta', label: t('Beta', 'Beta') },
                    { value: 'gamma', label: t('Gamma', 'Gamma') },
                  ]}
                />
              </fieldset>
              <fieldset>
                <Select
                  id='multiExample'
                  label={t('Select (multiple)', 'Seleccionar (multiple)')}
                  multiple
                  aria-describedby='multiHelp'
                  hintId='multiHelp'
                  options={[
                    {
                      value: '',
                      label: t('Select options', 'Selecciona opciones'),
                      placeholder: true,
                    },
                    { value: 'north', label: t('North', 'Norte') },
                    { value: 'south', label: t('South', 'Sur') },
                    { value: 'east', label: t('East', 'Este') },
                    { value: 'west', label: t('West', 'Oeste') },
                  ]}
                  hint={t('Select more than one.', 'Selecciona más de uno.')}
                />
              </fieldset>
              <fieldset>
                <Radios
                  name='options'
                  legend={t('Radios', 'Opciones')}
                  selectedValue={selectedOption ?? ''}
                  onChange={setSelectedOption}
                  options={[
                    { id: 'option1', value: t('Option 1', 'Opción 1') },
                    { id: 'option2', value: t('Option 2', 'Opción 2') },
                    { id: 'option3', value: t('Option 3', 'Opción 3') },
                  ]}
                />
              </fieldset>
              <fieldset>
                <Checkboxes
                  legend={t('Checkboxes', 'Casillas')}
                  selectedValues={selectedValues}
                  onChange={setSelectedValues}
                  options={[
                    { id: 'option1', label: t('Option 1', 'Opción 1') },
                    { id: 'option2', label: t('Option 2', 'Opción 2') },
                    { id: 'option3', label: t('Option 3', 'Opción 3') },
                  ]}
                />
              </fieldset>
              <fieldset>
                <ToggleSwitch
                  id='toggleSwitchExample'
                  label={t('Toggle switch', 'Interruptor')}
                  checked={toggleEnabled}
                  onChange={setToggleEnabled}
                />
              </fieldset>
              <fieldset>
                <Range
                  id='rangeExample'
                  outputId='rangeOut'
                  value={rangeValue}
                  min={0}
                  max={100}
                  step={1}
                  onChange={setRangeValue}
                />
              </fieldset>
              <fieldset>
                <Input id='fileExample' type='file' label={t('File input', 'Input de archivo')} />
              </fieldset>
              <fieldset>
                <Textarea
                  id='textareaExample'
                  rows={3}
                  placeholder={t('Notes…', 'Notas…')}
                  label={t('Textarea', 'Área de texto')}
                />
              </fieldset>
              <fieldset>
                <Input
                  id='invalidExample'
                  className='is-invalid'
                  defaultValue='bad@email'
                  type='email'
                  label={t('Invalid example', 'Ejemplo invalido')}
                  hintClassName='error'
                  hint={t('Please enter a valid email.', 'Ingresa un correo valido.')}
                />
              </fieldset>
              <fieldset>
                <Input
                  id='disabledExample'
                  label={t('Disabled Field', 'Campo deshabilitado')}
                  disabled
                  placeholder={t('Disabled state', 'Estado deshabilitado')}
                />
              </fieldset>
            </div>
          </Card>
        </div>
      </Container>
    </section>
  );
};

export default FormControls;
